import type { PaginatedTransactions } from '@finapp/types';
import { API_BASE_URL, ApiError, fetchWithAuth } from '../config/api';

export class TransactionsApiError extends ApiError {
  constructor(message: string, status?: number, details?: unknown) {
    super(message, status, details);
    this.name = 'TransactionsApiError';
  }
}

export interface TransactionListFilter {
  page?: number | undefined;
  limit?: number | undefined;
  year?: number | undefined;
  // Only meaningful alongside `year` - the backend 400s on month without year, since a month
  // alone is ambiguous.
  month?: number | undefined;
  // Free-text search across heading/original_heading/description - independent of year/month,
  // combined with AND when both are set (see TransactionRepositoryService.buildWhereClause).
  search?: string | undefined;
}

export const transactionsApi = {
  /**
   * Fetch a page of transactions, optionally scoped to a year (or year+month). Mirrors
   * forecastApi.ts/reportsApi.ts's convention for building/calling GET endpoints.
   */
  async getTransactions(filter: TransactionListFilter = {}): Promise<PaginatedTransactions> {
    const queryParams = new URLSearchParams();
    if (filter.page !== undefined) {
      queryParams.set('page', filter.page.toString());
    }
    if (filter.limit !== undefined) {
      queryParams.set('limit', filter.limit.toString());
    }
    if (filter.year !== undefined) {
      queryParams.set('year', filter.year.toString());
    }
    if (filter.month !== undefined) {
      queryParams.set('month', filter.month.toString());
    }
    if (filter.search) {
      queryParams.set('search', filter.search);
    }

    const qs = queryParams.toString();
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/v1/transactions${qs ? `?${qs}` : ''}`
    );

    const data = await response.json();
    return data as PaginatedTransactions;
  },
};
